/**
 * My work (PRD-tasks-asks-and-my-work §4.3, FR-3; owner ruling D1, 2026-11-03).
 *
 * One page for the things a person has, on the list shell every record uses
 * (EntityListPage, tiles across the top, a peek per row): what needs them now,
 * what they wait on, what they promised, what they own, what they follow, and
 * what ended lately. It replaces the Tasks page (/prioritization/my-tasks
 * redirects here). Needs you is the engine's list (get_responsibilities), the
 * same rows Home draws; nothing here counts them again.
 */
import { useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Inbox } from 'lucide-react';
import { EntityListPage, type EntityListTile } from '@/components/collection/EntityListPage';
import type { TableColumnWithRender } from '@/components/collection/types';
import type { SortOption } from '@/components/collection/types';
import { AssignmentAskAccept } from '@/components/prioritization/commitments/AssignmentAskAccept';
import { AskButton } from '@/components/ask/AskSheet';
import { RemindButton } from '@/components/ask/RemindButton';
import { CardQuickAction } from '@/components/responsibility/CardQuickAction';
import { quickActionOf } from '@/lib/responsibilityQuickAction';
import { announceNotesChanged, completeNote, deleteNote, reopenNote } from '@/lib/notesService';
import { assignmentAcceptOf } from '@/lib/assignmentService';
import { useUserStore } from '@/stores/prioritization/userStore';
import { useTenantStore } from '@/stores/prioritization/tenantStore';
import { useMyWork, type WorkKind, type WorkRow } from './useMyWork';

const KIND_WORDS: Record<WorkKind, string> = {
  'needs-you': 'Needs you',
  waiting: 'Waiting on others',
  promise: 'Promises',
  owned: 'Owned',
  following: 'Following',
  note: 'Notes',
  done: 'Done',
};

const TILES: EntityListTile<WorkRow>[] = [
  { id: 'needs-you', label: KIND_WORDS['needs-you'], match: (r) => r.kind === 'needs-you', tone: (n) => (n > 0 ? 'coral' : 'quiet') },
  { id: 'promise', label: KIND_WORDS.promise, match: (r) => r.kind === 'promise', tone: 'neutral', hint: 'What you said you would do' },
  { id: 'waiting', label: KIND_WORDS.waiting, match: (r) => r.kind === 'waiting', tone: 'neutral', hint: 'What you asked of others, and what waits on them' },
  { id: 'owned', label: KIND_WORDS.owned, match: (r) => r.kind === 'owned', tone: 'neutral' },
  { id: 'following', label: KIND_WORDS.following, match: (r) => r.kind === 'following', tone: 'neutral' },
  { id: 'note', label: KIND_WORDS.note, match: (r) => r.kind === 'note', tone: 'neutral', hint: 'What you wrote down for yourself' },
  { id: 'done', label: KIND_WORDS.done, match: (r) => r.kind === 'done', tone: 'quiet', hint: 'The last thirty days' },
];

const SORT_OPTIONS: SortOption<string>[] = [
  { field: 'dueSort', label: 'Due' },
  { field: 'title', label: 'Name' },
  { field: 'what', label: 'Kind' },
];

const EMPTY: Record<WorkKind | 'all', { title: string; body: string }> = {
  'needs-you': { title: 'Nothing needs you right now', body: 'When a session, a ball, a request or a promise of yours comes due needs your part, it is listed here and on Home.' },
  waiting: { title: 'You are not waiting on anyone', body: 'What you ask of others, and what waits on them after you answered, is listed here until they answer.' },
  promise: { title: 'No promises in force', body: 'When you accept a request, it is listed here until it is done, released or renegotiated.' },
  owned: { title: 'You own nothing open here', body: 'Items, cycles and sessions you own or facilitate, and decisions you made that come up for review.' },
  following: { title: 'You are following nothing', body: 'Follow an item, a cycle, a track, a session, a decision or a commitment from its page; it is listed here, and its changes reach you as updates.' },
  note: { title: 'No notes', body: 'Ask, then To remember it yourself, writes a note only you see. Give it a date and it comes back to you that day.' },
  done: { title: 'Nothing ended in the last thirty days', body: 'Completed tasks and kept promises are listed here for thirty days.' },
  all: { title: 'Nothing here yet', body: 'What needs you, what you promised, what you own and what you follow.' },
};

const DEFAULT_TILE = 'needs-you';

/** The URL says notes; the tile is note. */
const TILE_ALIASES: Record<string, string> = { notes: 'note' };

function dueWords(r: WorkRow): string {
  if (!r.dueAt) return '';
  const d = new Date(r.dueAt.length === 10 ? `${r.dueAt}T12:00:00Z` : r.dueAt);
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

export function MyWorkPage() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const userId = useUserStore((s) => s.userId);
  const workspaceId = useTenantStore((s) => s.currentWorkspaceId);
  const { rows, loading, error, refresh } = useMyWork(userId, workspaceId);

  const showParam = params.get('show');
  const activeTileId = showParam === 'all' ? null : (showParam && (TILE_ALIASES[showParam] ?? showParam)) ?? DEFAULT_TILE;
  const setActiveTile = useCallback((id: string | null) => {
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      if (id === DEFAULT_TILE) next.delete('show');
      else next.set('show', id ?? 'all');
      return next;
    }, { replace: true });
  }, [setParams]);

  const empty = EMPTY[(activeTileId as WorkKind | null) ?? 'all'] ?? EMPTY.all;

  const tableColumns = useMemo((): TableColumnWithRender<WorkRow>[] => [
    {
      key: 'title',
      label: 'What',
      sortable: true,
      minWidth: 260,
      flex: true,
      render: (r) => (
        <div className="min-w-0">
          <span className="block truncate text-sm font-medium text-[var(--text-pri)]">{r.title}</span>
          {r.detail && <span className="block truncate text-xs text-[var(--text-ter)]">{r.detail}</span>}
        </div>
      ),
    },
    { key: 'what', label: 'Kind', sortable: true, minWidth: 96, render: (r) => <span className="text-xs text-[var(--text-sec)]">{r.what}</span> },
    { key: 'dueSort', label: 'Due', sortable: true, minWidth: 90, render: (r) => <span className="text-xs text-[var(--text-sec)]">{dueWords(r) || '—'}</span> },
    ...(activeTileId === null
      ? [{ key: 'kind', label: 'In', sortable: true, minWidth: 110, render: (r: WorkRow) => <span className="text-xs text-[var(--text-ter)]">{KIND_WORDS[r.kind]}</span> }]
      : []),
  ], [activeTileId]);

  return (
    <EntityListPage<WorkRow, string>
      items={rows}
      loading={loading}
      error={error}
      getId={(r) => r.id}
      masterDetail={false}
      title="My work"
      subtitle="What needs you, what you promised, what you asked of others, what you own and what you follow."
      icon={<Inbox className="h-4 w-4 text-[var(--text-ter)]" />}
      itemLabel="thing"
      searchFields={['title', 'detail', 'what']}
      searchPlaceholder="Search my work…"
      sortOptions={SORT_OPTIONS}
      defaultSort={{ field: 'dueSort', direction: 'asc' }}
      tableColumns={tableColumns}
      viewModes={['table', 'card']}
      defaultViewMode="table"
      renderCard={(r) => <WorkCard row={r} />}
      selectable={false}
      tiles={TILES}
      activeTileId={activeTileId}
      onActiveTileChange={setActiveTile}
      onItemClick={(r) => { if (r.href) navigate(r.href); }}
      renderPeek={(r) => <WorkPeek row={r} onDone={refresh} onOpen={() => r.href && navigate(r.href)} />}
      onRefresh={refresh}
      headerActions={<AskButton home={{ type: 'statement', id: null }} label="Ask someone" />}
      emptyTitle={empty.title}
      emptyDescription={empty.body}
      shellClassName="h-[calc(100vh-6rem)]"
    />
  );
}

function WorkCard({ row }: { row: WorkRow }) {
  return (
    <div className="space-y-1 p-3">
      <div className="font-mono text-[11px] uppercase tracking-[0.08em] text-[var(--text-ter)]">
        {row.what}{dueWords(row) ? ` · ${dueWords(row)}` : ''}
      </div>
      <div className="text-sm font-medium text-[var(--text-pri)]">{row.title}</div>
      {row.detail && <div className="text-xs text-[var(--text-sec)]">{row.detail}</div>}
    </div>
  );
}

function NoteActions({ row, onDone }: { row: WorkRow; onDone: () => void }) {
  const note = row.note;
  if (!note) return null;
  const act = async (fn: () => Promise<{ ok: boolean; error?: string }>) => {
    const res = await fn();
    if (res.ok) { announceNotesChanged(); onDone(); }
  };
  const btn = 'text-xs px-2.5 py-1 border border-[var(--rule)] hover:bg-[var(--hover-tint)]';
  return (
    <>
      {note.done_at
        ? <button type="button" className={btn} onClick={() => void act(() => reopenNote(note.id))}>Reopen</button>
        : <button type="button" className={btn} onClick={() => void act(() => completeNote(note.id))}>Done</button>}
      <button type="button" className={`${btn} text-[var(--text-ter)]`} onClick={() => void act(() => deleteNote(note.id))}>Delete</button>
    </>
  );
}

function WorkPeek({ row, onDone, onOpen }: { row: WorkRow; onDone: () => void; onOpen: () => void }) {
  const accept = row.card && assignmentAcceptOf(row.card) ? row.card : null;
  const quick = row.card && quickActionOf(row.card) ? row.card : null;
  const action = row.card?.valid_actions?.find((a) => !a.rpc)?.label;
  return (
    <div className="space-y-2 py-1 text-sm">
      {row.note && <p className="whitespace-pre-wrap text-[var(--text-pri)]">{row.note.body}</p>}
      {row.detail && <p className="text-[var(--text-sec)]">{row.detail}</p>}
      <div className="flex flex-wrap items-center gap-3">
        {accept && <AssignmentAskAccept card={accept} onDone={onDone} />}
        {quick && <CardQuickAction card={quick} onDone={() => { announceNotesChanged(); onDone(); }} />}
        <NoteActions row={row} onDone={onDone} />
        {row.remind && <RemindButton key={row.id} type={row.remind.type} id={row.remind.id} />}
        {row.href && (
          <button type="button" onClick={onOpen} className="text-xs underline text-[var(--text-sec)] hover:text-[var(--text-pri)]">
            {action ?? 'Open'} →
          </button>
        )}
      </div>
    </div>
  );
}

export default MyWorkPage;
